import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

interface PreloaderProps {
  loading: boolean;
  onComplete?: () => void;
}

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const PreloaderContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isDisappearing'].includes(prop)
})<{ isDisappearing: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: opacity 0.5s ease;
  animation: ${props => props.isDisappearing ? fadeOut : 'none'} 0.5s forwards;
`;

const LogoContainer = styled.div`
  margin-bottom: 30px;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

const Logo = styled.img`
  max-width: 200px;
  height: auto;
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 10px;
  background-color: #f0f0f0;
  border-radius: 5px;
  overflow: hidden;
  margin-top: 20px;
`;

const ProgressBar = styled.div<{ width: number }>`
  height: 100%;
  width: ${props => props.width}%;
  background: linear-gradient(90deg, var(--primary-blue) 0%, var(--primary-orange) 100%);
  border-radius: 5px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: var(--primary-blue);
  font-weight: bold;
`;

const Preloader: React.FC<PreloaderProps> = ({ loading, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isDisappearing, setIsDisappearing] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (loading) {
      // Asegurarse de que el preloader sea visible cuando loading es true
      setIsVisible(true);
      setIsDisappearing(false);
      
      // Simular progreso mientras se cargan los recursos
      interval = setInterval(() => {
        setProgress(prevProgress => {
          // Incrementar gradualmente hasta 90% mientras loading es true
          if (prevProgress < 90) {
            return prevProgress + Math.random() * 10;
          }
          return prevProgress;
        });
      }, 200);
    } else {
      // Cuando loading es false, completar rápidamente hasta 100%
      setProgress(100);
      
      // Dar más tiempo para que los elementos se rendericen correctamente en móvil
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
      const extraDelay = isMobile ? 800 : 300; // Más tiempo en móvil
      
      // Iniciar animación de desaparición
      setTimeout(() => {
        setIsDisappearing(true);
        
        // Eliminar completamente el preloader del DOM después de la animación
        setTimeout(() => {
          setIsVisible(false);
          if (onComplete) onComplete();
        }, 500); // Duración de la animación fadeOut
      }, extraDelay);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading, onComplete]);

  // Si no es visible, no renderizar nada
  if (!isVisible) return null;

  return (
    <PreloaderContainer isDisappearing={isDisappearing}>
      <LogoContainer>
        <Logo src="/logo-vargas-homecenter2.png" alt="Vargas Homecenter" />
      </LogoContainer>
      <ProgressBarContainer>
        <ProgressBar width={progress} />
      </ProgressBarContainer>
      <ProgressText>{Math.round(progress)}%</ProgressText>
    </PreloaderContainer>
  );
};

export default Preloader;